import type { TFunction } from "i18next";

import {
  getDiagnosticChartOfAccounts,
  type CoaAction,
  type CoaDiagnosticItem,
  type CoaDiagnosticQuery,
  type CoaStatus,
} from "@/api/diagnostic-chart-of-accounts";
import { finalAccount } from "@/components/projects/diagnostic/selectors/chartOfAccounts";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";

/**
 * A lista do plano de contas para o cliente baixar (#1715, cartão 7.6 do Marco).
 *
 * Qualquer usuário do projeto baixa a lista resumida do detalhe do Diagnóstico,
 * só com as colunas do item 8.1 da EF-FUNC-009 e com os filtros da tela. É um
 * arquivo simples: a planilha de curadoria, protegida e completa, continua só do
 * consultor.
 *
 * A guarda contra fórmula é gravar tudo como texto, como a exportação dos scope
 * items: o SheetJS escreve string como célula `s`, e o Excel a abre como texto
 * mesmo começando com `=`.
 */

export interface CoaListLabels {
  status: Record<CoaStatus, string>;
  action: Record<CoaAction, string>;
}

const COLUMN_WIDTHS = [14, 48, 30, 26, 16, 24, 60];
/** O teto do `page_size` da rota: a AL5 tem 1.116 contas, então três páginas. */
const PAGE_SIZE = 500;

export function chartOfAccountsMatrix(
  items: CoaDiagnosticItem[],
  t: TFunction,
  labels: CoaListLabels,
): string[][] {
  const header = [
    t("Client account"),
    t("Description"),
    t("Status"),
    t("Action"),
    t("Mirror account"),
    t("Final account in YCOA"),
    t("Consultant note"),
  ];
  const body = items.map((item) => {
    const final = finalAccount(item);
    const action = labels.action[item.effective_decision];
    return [
      item.account,
      item.description ?? "",
      `${item.status} · ${labels.status[item.status]}`,
      item.decision == null ? t("{{action}} (suggested)", { action }) : action,
      item.effective_mirror_account ?? "",
      final.kind === "same_as_ycoa"
        ? t("Same as YCOA ({{account}})", { account: final.account })
        : final.kind === "account"
          ? final.account
          : NO_VALUE,
      item.note ?? "",
    ];
  });
  return [header, ...body];
}

/**
 * `myGROW_Diagnostic_ChartOfAccounts_<projeto>_AAAAMMDD_HHMM.xlsx`, no padrão da
 * exportação dos scope items: o que não é letra nem número no nome vira `_`.
 */
export function chartOfAccountsFileName(projectName: string | undefined, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const project = (projectName ?? "").replace(/[^\p{L}\p{N}-]+/gu, "_").replace(/^_+|_+$/g, "");
  return ["myGROW_Diagnostic_ChartOfAccounts", project, stamp].filter(Boolean).join("_") + ".xlsx";
}

/** Todas as contas que passam nos filtros da tela, página a página. */
async function allItems(projectId: string, query: CoaDiagnosticQuery): Promise<CoaDiagnosticItem[]> {
  const items: CoaDiagnosticItem[] = [];
  for (let page = 1; ; page += 1) {
    const result = await getDiagnosticChartOfAccounts(projectId, {
      ...query,
      page,
      pageSize: PAGE_SIZE,
    });
    items.push(...result.items);
    if (result.items.length === 0 || items.length >= result.total) return items;
  }
}

/** Baixa a planilha; o SheetJS só carrega no clique. */
export async function downloadChartOfAccountsWorkbook(
  projectId: string,
  query: CoaDiagnosticQuery,
  t: TFunction,
  labels: CoaListLabels,
  projectName: string | undefined,
): Promise<void> {
  const items = await allItems(projectId, query);
  const XLSX = await import("xlsx");
  const sheet = XLSX.utils.aoa_to_sheet(chartOfAccountsMatrix(items, t, labels));
  sheet["!cols"] = COLUMN_WIDTHS.map((wch) => ({ wch }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, t("Chart of accounts"));
  XLSX.writeFile(workbook, chartOfAccountsFileName(projectName, new Date()));
}
